import { useState, useEffect, useCallback } from 'react';
import { ApiError } from '@yawi/infrastructure';
import { ACCESS_LEVEL_ERROR_CODES } from '@yawi/types';
import type { AccessLevel } from '@yawi/types';
import { useServices } from '@/app/providers/ServiceContext';
import { useToast, getErrorMessage } from '@/app/providers';

export function useAccessLevels(integrationId: string) {
  const { accessLevelRepo } = useServices();
  const toast = useToast();

  const [accessLevels, setAccessLevels] = useState<AccessLevel[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshedAt, setRefreshedAt] = useState<number | null>(null);

  const loadList = useCallback(async () => {
    try {
      const data = await accessLevelRepo.list();
      setAccessLevels(data);
      setError(null);
    } catch (e) {
      setError(getErrorMessage(e));
    }
  }, [accessLevelRepo]);

  useEffect(() => {
    setLoading(true);
    loadList().finally(() => setLoading(false));
  }, [loadList]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      await accessLevelRepo.refresh();
      setRefreshedAt(Date.now());
      await loadList();
      toast.success('Niveles de acceso actualizados');
    } catch (e) {
      if (ApiError.isApiError(e) && e.statusCode === 429) {
        toast.warning('Refrescado recientemente. Espera un minuto.', 'Límite alcanzado');
      } else {
        toast.error(getErrorMessage(e));
      }
    } finally {
      setLoading(false);
    }
  }, [accessLevelRepo, loadList, toast]);

  const setDefault = useCallback(
    async (accessLevelId: string): Promise<boolean> => {
      setUpdating(true);
      try {
        await accessLevelRepo.setDefault(integrationId, accessLevelId);
        setAccessLevels((prev) =>
          prev.map((al) => ({ ...al, isDefault: al.id === accessLevelId })),
        );
        toast.success('Nivel de acceso predeterminado actualizado');
        return true;
      } catch (e) {
        if (ApiError.isApiError(e)) {
          switch (e.code) {
            case ACCESS_LEVEL_ERROR_CODES.NOT_FOUND:
              toast.error('El nivel de acceso seleccionado no existe en HikCentral.');
              break;
            case ACCESS_LEVEL_ERROR_CODES.PROVIDER_UNSUPPORTED:
              toast.error('Este proveedor no soporta niveles de acceso.');
              break;
            default:
              toast.error(getErrorMessage(e));
          }
        } else {
          toast.error(getErrorMessage(e));
        }
        return false;
      } finally {
        setUpdating(false);
      }
    },
    [accessLevelRepo, integrationId, toast],
  );

  return {
    accessLevels,
    loading,
    updating,
    error,
    refreshedAt,
    refresh,
    setDefault,
    reload: loadList,
  };
}
